import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ActionPlan {
  id: string;
  title: string;
  description?: string;
  status: 'draft' | 'in_progress' | 'completed' | 'on_hold';
  priority: 'low' | 'medium' | 'high' | 'critical';
  createdDate: string;
  dueDate?: string;
  assignee?: string;
  items?: ActionPlanItem[];
}

export interface ActionPlanItem {
  id: string;
  title: string;
  description?: string;
  status: 'pending' | 'in_progress' | 'completed';
  assignee?: string;
  dueDate?: string;
  completion?: number; // 0-100
}

export interface ToastNotification {
  header: string;
  subtext: string;
  variant: 'success' | 'error' | 'warning' | 'info';
  position?: 'top' | 'bottom' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  duration?: number;
}

// New: central error type for action plan module
export interface ActionPlanError {
  id: string;
  message: string;
  details?: string;
  apiName?: string;
  timestamp?: number;
}

export interface ActionPlanState {
  actionPlans: ActionPlan[];
  currentActionPlan: ActionPlan | null;
  loading: boolean;
  error: string | null;
  filters: {
    status?: string;
    priority?: string;
    searchTerm?: string;
  };
  toastNotification: ToastNotification | null;
  // Centralized errors follow the same pattern used across other slices
  centralizedErrors: {
    errors: ActionPlanError[];
    activeError: ActionPlanError | null;
    isErrorModalVisible: boolean;
  };
}

const initialState: ActionPlanState = {
  actionPlans: [],
  currentActionPlan: null,
  loading: false,
  error: null,
  filters: {
    status: undefined,
    priority: undefined,
    searchTerm: undefined,
  },
  toastNotification: null,
  centralizedErrors: {
    errors: [],
    activeError: null,
    isErrorModalVisible: false,
  },
};

const actionPlanSlice = createSlice({
  name: 'actionPlan',
  initialState,
  reducers: {
    // Set all action plans
    setActionPlans: (state, action: PayloadAction<ActionPlan[]>) => {
      state.actionPlans = action.payload;
      state.error = null;
    },

    // Add a new action plan
    addActionPlan: (state, action: PayloadAction<ActionPlan>) => {
      state.actionPlans.push(action.payload);
      state.error = null;
    },

    // Update an existing action plan
    updateActionPlan: (state, action: PayloadAction<ActionPlan>) => {
      const index = state.actionPlans.findIndex(
        (plan) => plan.id === action.payload.id
      );
      if (index !== -1) {
        state.actionPlans[index] = action.payload;
      }
      if (
        state.currentActionPlan &&
        state.currentActionPlan.id === action.payload.id
      ) {
        state.currentActionPlan = action.payload;
      }
      state.error = null;
    },

    // Delete an action plan
    deleteActionPlan: (state, action: PayloadAction<string>) => {
      state.actionPlans = state.actionPlans.filter(
        (plan) => plan.id !== action.payload
      );
      if (
        state.currentActionPlan &&
        state.currentActionPlan.id === action.payload
      ) {
        state.currentActionPlan = null;
      }
      state.error = null;
    },

    // Set the current action plan
    setCurrentActionPlan: (state, action: PayloadAction<ActionPlan | null>) => {
      state.currentActionPlan = action.payload;
      state.error = null;
    },

    // Add an item to the current action plan
    addActionPlanItem: (state, action: PayloadAction<ActionPlanItem>) => {
      if (state.currentActionPlan) {
        if (!state.currentActionPlan.items) {
          state.currentActionPlan.items = [];
        }
        state.currentActionPlan.items.push(action.payload);
      }
    },

    // Update an item in the current action plan
    updateActionPlanItem: (
      state,
      action: PayloadAction<{ planId: string; item: ActionPlanItem }>
    ) => {
      const plan = state.actionPlans.find(
        (p) => p.id === action.payload.planId
      );
      if (plan && plan.items) {
        const itemIndex = plan.items.findIndex(
          (i) => i.id === action.payload.item.id
        );
        if (itemIndex !== -1) {
          plan.items[itemIndex] = action.payload.item;
        }
      }

      if (
        state.currentActionPlan &&
        state.currentActionPlan.id === action.payload.planId
      ) {
        if (state.currentActionPlan.items) {
          const itemIndex = state.currentActionPlan.items.findIndex(
            (i) => i.id === action.payload.item.id
          );
          if (itemIndex !== -1) {
            state.currentActionPlan.items[itemIndex] = action.payload.item;
          }
        }
      }
    },

    // Delete an item from an action plan
    deleteActionPlanItem: (
      state,
      action: PayloadAction<{ planId: string; itemId: string }>
    ) => {
      const plan = state.actionPlans.find(
        (p) => p.id === action.payload.planId
      );
      if (plan && plan.items) {
        plan.items = plan.items.filter(
          (i) => i.id !== action.payload.itemId
        );
      }

      if (
        state.currentActionPlan &&
        state.currentActionPlan.id === action.payload.planId
      ) {
        if (state.currentActionPlan.items) {
          state.currentActionPlan.items = state.currentActionPlan.items.filter(
            (i) => i.id !== action.payload.itemId
          );
        }
      }
    },

    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    // Set error state
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Update filters
    setFilters: (
      state,
      action: PayloadAction<Partial<ActionPlanState['filters']>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    // Clear all data
    clearActionPlanData: (state) => {
      state.actionPlans = [];
      state.currentActionPlan = null;
      state.loading = false;
      state.error = null;
      state.filters = {
        status: undefined,
        priority: undefined,
        searchTerm: undefined,
      };
    },

    // Set toast notification
    setToastNotification: (state, action: PayloadAction<ToastNotification | null>) => {
      state.toastNotification = action.payload;
    },

    // Clear toast notification
    clearToastNotification: (state) => {
      state.toastNotification = null;
    },

    // Centralized error handling (mirror pattern from observation/standup/etc.)
    addCentralizedError: (state, action: PayloadAction<ActionPlanError>) => {
      state.centralizedErrors.errors.push(action.payload);
      // keep last 10 errors to avoid memory growth
      if (state.centralizedErrors.errors.length > 10) {
        state.centralizedErrors.errors = state.centralizedErrors.errors.slice(-10);
      }
    },

    showErrorModal: (state, action: PayloadAction<ActionPlanError>) => {
      state.centralizedErrors.activeError = action.payload;
      state.centralizedErrors.isErrorModalVisible = true;
      // also add to list
      state.centralizedErrors.errors.push(action.payload);
      if (state.centralizedErrors.errors.length > 10) {
        state.centralizedErrors.errors = state.centralizedErrors.errors.slice(-10);
      }
    },

    hideErrorModal: (state) => {
      state.centralizedErrors.isErrorModalVisible = false;
      state.centralizedErrors.activeError = null;
    },

    setActiveError: (state, action: PayloadAction<ActionPlanError | null>) => {
      state.centralizedErrors.activeError = action.payload;
    },

    clearAllErrors: (state) => {
      state.centralizedErrors.errors = [];
      state.centralizedErrors.activeError = null;
      state.centralizedErrors.isErrorModalVisible = false;
    },

    removeErrorById: (state, action: PayloadAction<string>) => {
      state.centralizedErrors.errors = state.centralizedErrors.errors.filter(
        (e) => e.id !== action.payload
      );
      if (state.centralizedErrors.activeError?.id === action.payload) {
        state.centralizedErrors.activeError = null;
        state.centralizedErrors.isErrorModalVisible = false;
      }
    },
  },
});

export const {
  setActionPlans,
  addActionPlan,
  updateActionPlan,
  deleteActionPlan,
  setCurrentActionPlan,
  addActionPlanItem,
  updateActionPlanItem,
  deleteActionPlanItem,
  setLoading,
  setError,
  setFilters,
  clearActionPlanData,
  setToastNotification,
  clearToastNotification,
  // centralized error actions
  addCentralizedError,
  showErrorModal,
  hideErrorModal,
  setActiveError,
  clearAllErrors,
  removeErrorById,
} = actionPlanSlice.actions;

export default actionPlanSlice.reducer;
